import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { PageFilter } from '../../../state/app';
import { AppState } from '../../../state/configureStore';
import {
  clearCustomerGroup,
  CustomerGroup,
  CustomerGroupListFilter,
  CustomerGroupState,
  fetchCustomerGroupList,
} from '../../../state/customerGroup';
import { PageContainer, PageTitle, SearchField, TableLoading } from '../../components';
import { useFilter, useHasRole } from '../../hooks';
import roleRoute from '../roleRoute.config';
import useStyles from './customerGroupList.style';

const CustomerGroupList: React.FC<RouteComponentProps> = (props) => {
  const dispatch = useDispatch();
  const { history } = props;
  const classes = useStyles();
  const [filters, setFilters] = useFilter<CustomerGroupListFilter>({});
  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 15,
  });

  const isDisabled = !useHasRole(roleRoute.createCustomerGroup);

  const {
    items: customerGroups,
    totalItems,
    loading,
  } = useSelector<AppState, CustomerGroupState>((state) => state.customerGroup);

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { target } = event;
    setFilters((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  useEffect(() => {
    dispatch(fetchCustomerGroupList(filters, paging));
    return function cleanUp() {
      dispatch(clearCustomerGroup());
    };
  }, [dispatch, paging, filters]);

  const addNewCustomerGroup = (): void => {
    history.push('/customerGroups/form');
  };

  const editCustomerGroup = (id: CustomerGroup['id']): void => {
    history.push(`/customerGroups/form/${id}`);
  };

  return (
    <PageContainer>
      <PageTitle>Customer Group</PageTitle>
      <div className={classes.filterContainer}>
        <SearchField
          label="Company Id"
          filterKey={'Company.ExternalId.startsWith'}
          filters={filters}
          onChange={handleChangeFilter}
        />
        <SearchField
          label="Company Id"
          filterKey={'Company.Name.startsWith'}
          filters={filters}
          onChange={handleChangeFilter}
        />
        <SearchField
          label="Name"
          filterKey={'Name.startsWith'}
          filters={filters}
          onChange={handleChangeFilter}
        />
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={addNewCustomerGroup}
          size="large"
          disabled={isDisabled}
        >
          <AddIcon className={classes.iconButton} />
          New CustomerGroup
        </Button>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>No.</TableCell>
            <TableCell>Company Id</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Customer Group Name</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableLoading isLoading={loading} colSpan={5}>
            {customerGroups.map((customerGroup, index) => (
              <TableRow key={customerGroup.id}>
                <TableCell>{index + paging.pageSize * paging.pageNumber + 1}</TableCell>
                <TableCell>{customerGroup.company!!.externalId}</TableCell>
                <TableCell>{customerGroup.company!!.name}</TableCell>
                <TableCell>{customerGroup.name}</TableCell>
                <TableCell>
                  <IconButton
                    aria-label="edit"
                    size="small"
                    onClick={() => editCustomerGroup(customerGroup.id)}
                    disabled={isDisabled}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableLoading>
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[15, 25, 35]}
        component="div"
        count={totalItems}
        rowsPerPage={paging.pageSize}
        page={paging.pageNumber}
        onPageChange={handleChangePageNumber}
        onRowsPerPageChange={handleChangePageSize}
      />
    </PageContainer>
  );
};

export default CustomerGroupList;
